/**
 * An item's place and `style` as CSS, pure: the keys the board writes (`fill`, `stroke`,
 * `color`, `size`, `weight`, `align`, `font`, `radius`, `shape`; the look itself is
 * `shared/elements.ts`, which the GUI packs draw with too) and how a frame and an arrow look.
 * Unknown keys are left alone (another plugin may read them).
 */
import type { JSX } from "solid-js";
import type { BoardPlace } from "@protocol/BoardPlace";
import { frameLook, lookOf, type ElementDef, type Style } from "../../../shared/elements";

export type { Style } from "../../../shared/elements";
export { FONTS } from "../../../shared/elements";

/** The shapes a shape item may take. */
export const SHAPES = ["rectangle", "rounded", "ellipse", "diamond", "triangle"] as const;

/** Where a place puts an element in the layer (board units). */
export function placeCss(place: BoardPlace): JSX.CSSProperties {
  return {
    left: `${place.x}px`,
    top: `${place.y}px`,
    width: `${place.w}px`,
    height: `${place.h}px`,
    ...(place.rotate ? { transform: `rotate(${place.rotate}deg)` } : {}),
  };
}

/** The look of an item drawn as `element` from its style (`shared/elements.ts`, shared with the GUI packs). */
export function styleCss(element: ElementDef, style: Style): JSX.CSSProperties {
  return lookOf(element, style) as JSX.CSSProperties;
}

/** A frame's look: its fill, border and corners. */
export function frameCss(style: Style): JSX.CSSProperties {
  return frameLook(style) as JSX.CSSProperties;
}

/** An arrow's dashes (`style.line`: solid, dashed, dotted) as an SVG `stroke-dasharray`. */
export function dashesOf(line: string | undefined): string | undefined {
  return line === "dashed" ? "8 6" : line === "dotted" ? "2 5" : undefined;
}
